import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, push, onValue } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { Search, SlidersHorizontal, X, BookOpen, Bell, BellOff, Heart, User, Mic, MicOff } from "lucide-react";
import { update } from "firebase/database";

const CATEGORIES = [
  "All", "Books", "Electronics", "Cycles & Vehicles", "Room Essentials",
  "Clothing & Uniforms", "Sports & Fitness", "Musical Instruments",
  "Study Tools", "Furniture", "Kitchen Items", "Art & Craft", "Other"
];

const PRICE_RANGES = [
  { label: "Any Price", value: "any" },
  { label: "Under ₹500", value: "0-500" },
  { label: "₹500–2000", value: "500-2000" },
  { label: "₹2000–10000", value: "2000-10000" },
  { label: "Above ₹10000", value: "10000+" },
];

const CONDITIONS = ["All", "Like New", "Good", "Fair", "Used"];

const SORT_OPTIONS = [
  { label: "Newest", value: "newest" },
  { label: "Price: Low to High", value: "price_asc" },
  { label: "Price: High to Low", value: "price_desc" },
];

function ListingCard({ listing, currentUser, savedIds, onToggleSave }) {
  const isSold = listing.status === "sold";
  const isReserved = listing.status === "reserved";
  const isSaved = savedIds?.includes(listing.id);

  function handleSave(e) {
    e.preventDefault();
    e.stopPropagation();
    onToggleSave(listing.id, isSaved);
  }

  return (
    <Link to={`/listing/${listing.id}`} className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition block relative border border-gray-100">
      <div className="h-36 bg-gray-100 overflow-hidden relative">
        {listing.imageUrl ? (
          <img src={listing.imageUrl} alt={listing.title} className={`w-full h-full object-cover ${isSold ? "opacity-50" : ""}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen size={32} className="text-gray-200" />
          </div>
        )}
        {isSold && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="bg-red-600 text-white font-bold text-sm px-3 py-1 rounded-full rotate-[-15deg]">SOLD</span>
          </div>
        )}
        {isReserved && (
          <div className="absolute top-2 left-2">
            <span className="bg-yellow-500 text-white font-bold text-xs px-2 py-0.5 rounded-full">Reserved</span>
          </div>
        )}
        {listing.isRental && !isSold && (
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            <span className="bg-teal-500 text-white font-bold text-xs px-2 py-0.5 rounded-full">🏠 Rental</span>
            {listing.rental?.deliveryType === "delivery" && <span className="bg-blue-500 text-white font-bold text-[10px] px-1.5 py-0.5 rounded-full">🚚 Delivery</span>}
            {listing.rental?.deliveryType === "both" && <span className="bg-blue-500 text-white font-bold text-[10px] px-1.5 py-0.5 rounded-full">🔄 Del/Pickup</span>}
          </div>
        )}
        {listing.isBundle && !isSold && !isReserved && !listing.isRental && (
          <div className="absolute top-2 left-2">
            <span className="bg-purple-600 text-white font-bold text-xs px-2 py-0.5 rounded-full">📦 Bundle</span>
          </div>
        )}
        {currentUser && listing.sellerId !== currentUser.uid && (
          <button onClick={handleSave} className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow">
            <Heart size={14} className={isSaved ? "text-red-500 fill-red-500" : "text-gray-400"} />
          </button>
        )}
      </div>
      <div className="p-3">
        <p className={`font-semibold text-sm line-clamp-1 ${isSold ? "text-gray-400" : "text-gray-800"}`}>{listing.title}</p>
        <div className="mt-0.5">
          {listing.isRental && listing.rental && !isSold ? (
            <div className="flex flex-wrap gap-1 items-center">
              {listing.rental.pricePerDay && <span className="text-xs font-bold text-teal-600">₹{listing.rental.pricePerDay}/day</span>}
              {listing.rental.pricePerWeek && <span className="text-xs font-semibold text-teal-500">₹{listing.rental.pricePerWeek}/wk</span>}
              {listing.rental.pricePerMonth && <span className="text-xs font-semibold text-teal-500">₹{listing.rental.pricePerMonth}/mo</span>}
              {listing.rental.deposit > 0 && <span className="text-[10px] text-gray-400">+₹{listing.rental.deposit}dep</span>}
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <p className={`font-bold text-sm ${isSold ? "text-gray-400 line-through" : listing.isRental ? "text-teal-600" : "text-blue-600"}`}>
                {listing.price === 0 ? "Negotiable" : `₹${listing.price}`}
              </p>
              {listing.isRental && listing.rentalPeriod && !isSold && (
                <span className="text-xs text-teal-600 font-semibold">
                  {{ per_day: "/ day", per_week: "/ week", per_month: "/ month" }[listing.rentalPeriod] || ""}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between mt-2">
          {/* Seller avatar + name + verification badge */}
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="w-5 h-5 rounded-full bg-blue-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
              {listing.sellerPhotoURL
                ? <img src={listing.sellerPhotoURL} alt={listing.sellerName} className="w-full h-full object-cover" />
                : <span className="text-blue-600 font-bold text-[9px]">{listing.sellerName?.[0]?.toUpperCase() || "?"}</span>}
            </div>
            <span className="text-gray-500 text-xs truncate">{listing.sellerName || listing.college}</span>
            {listing.sellerVerified
              ? <span className="text-[9px] font-bold text-green-600 bg-green-50 px-1 py-0.5 rounded-full flex-shrink-0 border border-green-200">✓ ID Verified</span>
              : <span className="text-[9px] font-semibold text-gray-400 bg-gray-100 px-1 py-0.5 rounded-full flex-shrink-0">Unverified</span>
            }
          </div>
          <span className="bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ml-1">{listing.condition}</span>
        </div>
      </div>
    </Link>
  );
}

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, userProfile } = useAuth();
  const isExamOnly = userProfile?.examAspirant === true && userProfile?.userType !== "student";
  const [rawListings, setRawListings] = useState([]);
  const [listings, setListings] = useState([]);
  const [visibleCount, setVisibleCount] = useState(20);
  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [myCollegeOnly, setMyCollegeOnly] = useState(true);
  const [alertSaved, setAlertSaved] = useState(false);
  const [savedIds, setSavedIds] = useState([]);

  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") || "All");
  const [priceRange, setPriceRange] = useState("any");
  const [condition, setCondition] = useState("All");
  const [sortBy, setSortBy] = useState("newest");
  const [minRating, setMinRating] = useState(0);
  const [sellerRatings, setSellerRatings] = useState({});
  const [statusFilter, setStatusFilter] = useState("active");
  const [rentalOnly, setRentalOnly] = useState(false);
  const [searchTab, setSearchTab] = useState("listings"); // "listings" | "people"
  const [userResults, setUserResults] = useState([]);
  const userSearchTimer = useRef(null);

  // Recently viewed
  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("recentlyViewed") || "[]"); } catch { return []; }
  });

  // Search history
  const [searchHistory, setSearchHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("searchHistory") || "[]"); } catch { return []; }
  });
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const searchInputRef = useRef(null);

  function saveSearchHistory(term) {
    if (!term.trim() || term.trim().length < 2) return;
    setSearchHistory(prev => {
      const filtered = prev.filter(h => h.toLowerCase() !== term.toLowerCase());
      const updated = [term.trim(), ...filtered].slice(0, 8);
      localStorage.setItem("searchHistory", JSON.stringify(updated));
      return updated;
    });
  }

  function removeSearchHistory(term) {
    setSearchHistory(prev => {
      const updated = prev.filter(h => h !== term);
      localStorage.setItem("searchHistory", JSON.stringify(updated));
      return updated;
    });
  }

  // Voice search
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  function startVoiceSearch() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { alert("Voice search not supported on your browser."); return; }
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setSearchQuery(transcript);
      saveSearchHistory(transcript);
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }

  // Sync recentlyViewed from localStorage on mount/focus
  useEffect(() => {
    function syncRecent() {
      try { setRecentlyViewed(JSON.parse(localStorage.getItem("recentlyViewed") || "[]")); } catch {}
    }
    syncRecent();
    window.addEventListener("focus", syncRecent);
    return () => window.removeEventListener("focus", syncRecent);
  }, []);

  // Real-time listener for listings
  useEffect(() => {
    const unsub = onValue(ref(db, "listings"), (snap) => {
      const now = Date.now();
      let results = snap.exists()
        ? Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }))
        : [];
      results = results.map(l => {
        if (l.status === "reserved" && l.reservedUntil && l.reservedUntil < now) {
          return { ...l, status: "active", reservedBy: null, reservedUntil: null };
        }
        return l;
      });
      setRawListings(results);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (rawListings.length === 0) return;
    get(ref(db, "users")).then(snap => {
      if (!snap.exists()) return;
      const ratings = {};
      Object.entries(snap.val()).forEach(([uid, u]) => {
        if (u.rating) ratings[uid] = u.rating;
      });
      setSellerRatings(ratings);
    });
  }, [rawListings.length]);

  // Apply filters whenever rawListings or any filter state changes
  const applyFilters = useCallback(() => {
    let results = [...rawListings];
    if (myCollegeOnly && userProfile?.college) {
      results = results.filter(l => l.college === userProfile.college);
    }
    if (statusFilter !== "all") {
      results = results.filter(l => l.status === statusFilter);
    }
    results.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (activeCategory !== "All") {
      results = results.filter(l => l.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      results = results.filter(l =>
        l.title?.toLowerCase().includes(q) ||
        l.description?.toLowerCase().includes(q)
      );
    }
    if (condition !== "All") {
      results = results.filter(l => l.condition === condition);
    }
    if (priceRange !== "any") {
      if (priceRange === "0-500") results = results.filter(l => l.price <= 500);
      else if (priceRange === "500-2000") results = results.filter(l => l.price > 500 && l.price <= 2000);
      else if (priceRange === "2000-10000") results = results.filter(l => l.price > 2000 && l.price <= 10000);
      else if (priceRange === "10000+") results = results.filter(l => l.price > 10000);
    }
    if (minRating > 0) {
      results = results.filter(l => (sellerRatings[l.sellerId] || 0) >= minRating);
    }
    if (rentalOnly) {
      results = results.filter(l => l.isRental === true);
    }
    if (sortBy === "price_asc") results.sort((a, b) => a.price - b.price);
    else if (sortBy === "price_desc") results.sort((a, b) => b.price - a.price);
    setListings(results);
    setVisibleCount(20); // reset pagination on filter change
  }, [rawListings, activeCategory, searchQuery, priceRange, condition, sortBy, statusFilter, myCollegeOnly, userProfile, minRating, sellerRatings, rentalOnly]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);


  useEffect(() => {
    if (!currentUser) return;
    get(ref(db, `saved/${currentUser.uid}`)).then(snap => {
      if (snap.exists()) setSavedIds(Object.keys(snap.val()));
    });
  }, [currentUser]);

  // User search — debounced 400ms
  useEffect(() => {
    if (!searchQuery.trim()) { setUserResults([]); return; }
    clearTimeout(userSearchTimer.current);
    userSearchTimer.current = setTimeout(async () => {
      const q = searchQuery.toLowerCase();
      const snap = await get(ref(db, "users"));
      if (!snap.exists()) { setUserResults([]); return; }
      const matched = Object.entries(snap.val())
        .map(([uid, u]) => ({ uid, ...u }))
        .filter(u => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
        .slice(0, 10);
      setUserResults(matched);
    }, 400);
    return () => clearTimeout(userSearchTimer.current);
  }, [searchQuery]);

  // Infinite scroll — load 20 more when sentinel enters viewport
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMoreRef.current) {
          loadingMoreRef.current = true;
          setVisibleCount(v => {
            loadingMoreRef.current = false;
            return v + 20;
          });
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [listings]);

  async function toggleSave(listingId, isSaved) {
    if (!currentUser) return;
    if (isSaved) {
      await update(ref(db, `saved/${currentUser.uid}`), { [listingId]: null });
      setSavedIds(prev => prev.filter(id => id !== listingId));
    } else {
      await update(ref(db, `saved/${currentUser.uid}`), { [listingId]: true });
      setSavedIds(prev => [...prev, listingId]);
    }
  }

  function handleSearch(e) {
    e.preventDefault();
    saveSearchHistory(searchQuery);
    setShowSearchHistory(false);
  }

  async function saveAlert() {
    if (!currentUser) return;
    if (!searchQuery.trim() && activeCategory === "All") return;
    await push(ref(db, `alerts/${currentUser.uid}`), {
      keyword: searchQuery.trim(),
      category: activeCategory !== "All" ? activeCategory : null,
      college: myCollegeOnly ? userProfile?.college : null,
      createdAt: Date.now(),
    });
    setAlertSaved(true);
    setTimeout(() => setAlertSaved(false), 3000);
  }

  return (
    <div className="pb-20 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-600 sticky top-14 z-10">
        <div className="px-4 py-4">
          <p className="text-blue-200 text-xs mb-2 font-medium">🔍 Find what you need</p>
          <form onSubmit={handleSearch} className="relative flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                autoFocus
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setShowSearchHistory(true); }}
                onFocus={() => setShowSearchHistory(true)}
                onBlur={() => setTimeout(() => setShowSearchHistory(false), 150)}
                placeholder={isListening ? "Listening..." : "Search listings..."}
                className={`w-full pl-9 pr-9 py-2.5 border-0 rounded-xl text-sm focus:ring-2 focus:ring-blue-300 shadow-sm ${isListening ? "ring-2 ring-red-400" : ""}`}
              />
              {/* Search history dropdown */}
              {showSearchHistory && !searchQuery.trim() && searchHistory.length > 0 && (
                <div className="absolute top-full mt-1 left-0 right-0 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden">
                  <div className="px-3 py-2 flex items-center justify-between border-b border-gray-50">
                    <p className="text-xs font-semibold text-gray-400">Recent searches</p>
                    <button type="button" onClick={() => { setSearchHistory([]); localStorage.removeItem("searchHistory"); }} className="text-xs text-red-400 font-medium">Clear all</button>
                  </div>
                  {searchHistory.map(term => (
                    <div key={term} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 cursor-pointer" onMouseDown={() => { setSearchQuery(term); saveSearchHistory(term); setShowSearchHistory(false); }}>
                      <Search size={12} className="text-gray-300 flex-shrink-0" />
                      <span className="flex-1 text-sm text-gray-700 truncate">{term}</span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); removeSearchHistory(term); }} className="text-gray-300 hover:text-gray-500 flex-shrink-0">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={startVoiceSearch}
                className={`absolute right-2.5 top-1/2 -translate-y-1/2 ${isListening ? "text-red-500 animate-pulse" : "text-gray-400"}`}
              >
                {isListening ? <MicOff size={15} /> : <Mic size={15} />}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2.5 rounded-xl ${showFilters ? "bg-white text-blue-600" : "bg-white/20 text-white"}`}
            >
              <SlidersHorizontal size={18} />
            </button>
          </form>

          {/* College Toggle + Alert Me */}
          <div className="flex items-center justify-between mt-3">
            <button onClick={() => setMyCollegeOnly(!myCollegeOnly)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition ${myCollegeOnly ? "bg-white text-blue-600" : "bg-white/20 text-white"}`}>
              🏫 {myCollegeOnly ? (userProfile?.college ? userProfile.college.split(" ").slice(0,2).join(" ") : "My College") : "All Colleges"}
            </button>
            {(searchQuery.trim() || activeCategory !== "All") && (
              <button onClick={saveAlert}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition ${alertSaved ? "bg-green-400 text-white" : "bg-yellow-400 text-yellow-900"}`}>
                {alertSaved ? <><BellOff size={12} /> Saved!</> : <><Bell size={12} /> Alert Me</>}
              </button>
            )}
          </div>

          {/* Status Tabs */}
          <div className="flex gap-2 mt-2">
            {[{k:"active",l:"Available"},{k:"reserved",l:"Reserved"},{k:"sold",l:"Sold"},{k:"all",l:"All"}].map(({k,l}) => (
              <button key={k} onClick={() => setStatusFilter(k)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition flex-shrink-0 ${statusFilter === k ? "bg-white text-blue-600" : "bg-white/20 text-white"}`}>
                {l}
              </button>
            ))}
          </div>

          {/* Category Pills */}
          <div className="flex gap-2 overflow-x-auto mt-3 pb-3 no-scrollbar">
            {/* Rental toggle pill */}
            <button
              onClick={() => setRentalOnly(r => !r)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition border ${
                rentalOnly
                  ? "bg-teal-400 text-white border-teal-400"
                  : "bg-white/20 text-white border-white/30"
              }`}
            >
              🏠 Rentals
            </button>
            <div className="w-px h-6 bg-white/20 self-center flex-shrink-0" />
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  activeCategory === cat
                    ? "bg-white text-blue-600 font-bold"
                    : "bg-white/20 text-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="px-4 pb-4 bg-white border-t space-y-3">
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">Price Range</p>
              <div className="flex flex-wrap gap-2">
                {PRICE_RANGES.map(p => (
                  <button
                    key={p.value}
                    onClick={() => setPriceRange(p.value)}
                    className={`px-3 py-1 rounded-full text-xs border ${
                      priceRange === p.value ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">Condition</p>
              <div className="flex flex-wrap gap-2">
                {CONDITIONS.map(c => (
                  <button
                    key={c}
                    onClick={() => setCondition(c)}
                    className={`px-3 py-1 rounded-full text-xs border ${
                      condition === c ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">Sort By</p>
              <div className="flex flex-wrap gap-2">
                {SORT_OPTIONS.map(s => (
                  <button
                    key={s.value}
                    onClick={() => setSortBy(s.value)}
                    className={`px-3 py-1 rounded-full text-xs border ${
                      sortBy === s.value ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">Seller Rating</p>
              <div className="flex flex-wrap gap-2">
                {[{label:"Any Rating", val:0},{label:"★★★★+ (4+)", val:4},{label:"★★★+ (3+)", val:3},{label:"★★+ (2+)", val:2}].map(opt => (
                  <button key={opt.val} onClick={() => setMinRating(opt.val)}
                    className={`px-3 py-1 rounded-full text-xs border ${minRating === opt.val ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="px-4 pt-4 bg-gray-50">
        {/* Recently Viewed */}
        {!searchQuery.trim() && recentlyViewed.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">Recently Viewed</p>
              <button onClick={() => { setRecentlyViewed([]); localStorage.removeItem("recentlyViewed"); }} className="text-xs text-gray-400 hover:text-red-400">Clear</button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1 no-scrollbar">
              {recentlyViewed.map(item => (
                <Link key={item.id} to={`/listing/${item.id}`} className="flex-shrink-0 w-28 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition">
                  <div className="h-20 bg-gray-100 overflow-hidden">
                    {item.imageUrl
                      ? <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><BookOpen size={20} className="text-gray-200" /></div>}
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-semibold text-gray-700 line-clamp-1">{item.title}</p>
                    <p className="text-xs font-bold text-blue-600">{item.price === 0 ? "Negotiable" : `₹${item.price}`}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        {/* Exam aspirant notice */}
        {isExamOnly && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <p className="text-amber-800 font-semibold text-sm">🎯 You're in Exam Aspirant mode</p>
            <p className="text-amber-700 text-xs mt-1">This marketplace is for college & school students. Your zone is the <strong>Exam Prep Zone</strong> for study materials, groups, mentors & more.</p>
            <Link to="/exam-prep" className="inline-block mt-2 bg-orange-500 text-white text-xs font-bold px-4 py-1.5 rounded-full">Go to Exam Prep Zone →</Link>
          </div>
        )}
        {/* Tabs — only show when searching */}
        {searchQuery.trim() && (
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setSearchTab("listings")}
              className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition ${searchTab === "listings" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"}`}
            >
              Listings {!loading && `(${listings.length})`}
            </button>
            <button
              onClick={() => setSearchTab("people")}
              className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition ${searchTab === "people" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"}`}
            >
              People {userResults.length > 0 && `(${userResults.length})`}
            </button>
          </div>
        )}

        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-500">
            {loading ? "Searching..." : searchQuery.trim() && searchTab === "people" ? `${userResults.length} user${userResults.length !== 1 ? "s" : ""} found` : `${listings.length} listing${listings.length !== 1 ? "s" : ""} found`}
          </p>
          {(activeCategory !== "All" || searchQuery || condition !== "All" || priceRange !== "any") && (
            <button
              onClick={() => {
                setActiveCategory("All");
                setSearchQuery("");
                setCondition("All");
                setPriceRange("any");
                setSortBy("newest");
                setMinRating(0);
                setSearchTab("listings");
              }}
              className="flex items-center gap-1 text-xs text-red-500 font-medium"
            >
              <X size={12} /> Clear filters
            </button>
          )}
        </div>

        {/* People results */}
        {searchQuery.trim() && searchTab === "people" && (
          <div className="space-y-2 mb-4">
            {userResults.length === 0 ? (
              <div className="text-center py-16">
                <User size={40} className="mx-auto mb-3 text-gray-200" />
                <p className="text-gray-500 font-medium">No users found</p>
                <p className="text-gray-400 text-sm mt-1">Try searching by their full name</p>
              </div>
            ) : userResults.map(u => (
              <Link key={u.uid} to={`/seller/${u.uid}`}
                className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-3 hover:border-blue-200 hover:shadow-sm transition">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {u.photoURL
                    ? <img src={u.photoURL} alt={u.name} className="w-full h-full object-cover" />
                    : <span className="text-blue-700 font-bold text-lg">{u.name?.[0]?.toUpperCase() || "?"}</span>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 text-sm">{u.name}</p>
                  <p className="text-xs text-gray-500 truncate">{u.college || u.childSchool || "—"}</p>
                  {u.rating > 0 && (
                    <p className="text-xs text-yellow-500 mt-0.5">{"★".repeat(Math.round(u.rating))} <span className="text-gray-400">({u.totalRatings || 0} ratings)</span></p>
                  )}
                </div>
                <span className="text-xs text-blue-600 font-medium flex-shrink-0">View →</span>
              </Link>
            ))}
          </div>
        )}


        {(!searchQuery.trim() || searchTab === "listings") && loading ? (
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden animate-pulse">
                <div className="h-36 bg-gray-200" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : (!searchQuery.trim() || searchTab === "listings") && listings.length === 0 ? (
          <div className="text-center py-16">
            <Search size={40} className="mx-auto mb-3 text-gray-200" />
            <p className="text-gray-500 font-medium">No listings found</p>
            <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
            <Link
              to="/sell"
              className="mt-4 inline-block bg-blue-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold"
            >
              Post a Listing
            </Link>
          </div>
        ) : searchQuery.trim() && searchTab === "listings" ? (
          <div className="space-y-2">
            {listings.map(l => (
              <Link key={l.id} to={`/listing/${l.id}`}
                className="bg-white rounded-xl shadow-sm flex items-center gap-3 p-3 hover:bg-gray-50 transition border border-gray-100">
                <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0 relative">
                  {l.imageUrl
                    ? <img src={l.imageUrl} alt={l.title} className={`w-full h-full object-cover ${l.status === "sold" ? "opacity-50" : ""}`} />
                    : <div className="w-full h-full flex items-center justify-center"><BookOpen size={20} className="text-gray-300" /></div>}
                  {l.status === "sold" && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="bg-red-600 text-white font-bold text-xs px-1.5 py-0.5 rounded-full rotate-[-15deg]">SOLD</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold text-sm line-clamp-1 ${l.status === "sold" ? "text-gray-400" : "text-gray-800"}`}>{l.title}</p>
                  <p className={`font-bold text-sm mt-0.5 ${l.status === "sold" ? "text-gray-400 line-through" : "text-blue-600"}`}>
                    {l.price === 0 ? "Free" : `₹${l.price}`}
                  </p>
                  <p className="text-gray-400 text-xs mt-0.5 line-clamp-1">{l.college}</p>
                </div>
                {l.condition && <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full flex-shrink-0">{l.condition}</span>}
              </Link>
            ))}
          </div>
        ) : !searchQuery.trim() ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              {listings.slice(0, visibleCount).map(l => <ListingCard key={l.id} listing={l} currentUser={currentUser} savedIds={savedIds} onToggleSave={toggleSave} />)}
            </div>
            {/* Infinite scroll sentinel */}
            {listings.length > visibleCount && (
              <div ref={sentinelRef} className="flex justify-center py-4">
                <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {listings.length > 0 && listings.length <= visibleCount && (
              <p className="text-center text-xs text-gray-400 py-4">
                All {listings.length} listing{listings.length !== 1 ? "s" : ""} loaded
              </p>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
